import { useState } from "react"
import PropTypes from "prop-types"
import { AppBar, Tabs, Tab } from "@mui/material"
import { chain, map } from "lodash"
import { USER } from "helpers/shapes"
import { APPLICATION, TERMS_AND_CONDITIONS } from "./shapes"
import App from "./components/app"
import ATIntegration from "./components/at-integration"
import EzeIntegration from "./components/eze-integration"
import Integration from "./components/integration"
import AIIntegration from "./components/ai-integration"
import ApplicationIntegration from "./components/application-integration"
import { STATUSES, INTEGRATIONS } from "modules/platform/constants"
import "./style.scss"

const { NONE, REQUESTED, ACTIVE } = STATUSES
export default function Platform({
  apps,
  createToken,
  renameToken,
  hasApiAccess,
  deleteToken,
  addNotification,
  user,
  account,
  openDialog,
  closeDialog,
  legalAccountName,
  atIntegrationPermission,
  alphaTheoryStatus,
  alphaTheoryTerms,
  alphaTheoryTermsVersion,
  ezeIntegrationPermission,
  ezeStatus,
  ezeTerms,
  ezeTermsVersion,
  isSuperuser,
  requestIntegration,
  sortedIntegrations,
  partnerTerms,
  activateApplication,
  aiTerms,
  aiTermsVersion,
  aiIntegrationPermission,
  isAiPermissionGranted,
}) {
  const [tab, setTab] = useState(() => hasApiAccess ? 0 : 1)

  return <div className="modules_platform">
    <AppBar className="tabs-bar" position="static">
      <Tabs indicatorColor="primary" textColor="secondary" value={tab} onChange={(_, t) => setTab(t)} aria-label="Apps">
        <Tab label="Api" hidden={!hasApiAccess} />
        <Tab label="Applications" />
      </Tabs>
    </AppBar>
    <div className="apps">
      <div id="api" role="tabpanel" hidden={tab !== 0} aria-labelledby="api">
        {map(apps, (app) =>
          <App
            key={app.id}
            account={account}
            app={app}
            deleteTokenHandler={deleteToken}
            createTokenHandler={createToken}
            addNotification={addNotification}
            renameTokenHandler={renameToken}
            user={user}
          />,
        )}
      </div>

      <div id="apps" className="integrations" role="tabpanel" hidden={tab !== 1} aria-labelledby="apps">
        {map(sortedIntegrations, (integration, i) => {
          if (integration.type === INTEGRATIONS.GENERIC) {
            return (
              <Integration
                key={i}
                requestIntegration={requestIntegration}
                account={account}
                integration={integration}
              />
            )
          } else if (integration.type === INTEGRATIONS.PARTNER) {
            const termsText = chain(partnerTerms.value)
              .replace(/(?:\r\n|\r|\n)/g, "<br>")
              .replace("$legalName", `<b>${legalAccountName}</b>`)
              .replace(/\$partnerLegalName/g, `<b>${integration.name}</b>`)
              .value()

            return (
              <ApplicationIntegration
                key={i}
                application={integration}
                account={account}
                openDialog={openDialog}
                closeDialog={closeDialog}
                termsId={partnerTerms.id}
                termsText={termsText}
                isSuperuser={isSuperuser}
                activateApplication={activateApplication}
              />
            )
          } else if (integration.type === INTEGRATIONS.ALPHA_THEORY) {
            return (
              <ATIntegration
                key={i}
                atStatus={alphaTheoryStatus}
                atIntegrationPermission={atIntegrationPermission}
                account={account}
                user={user}
                openDialog={openDialog}
                closeDialog={closeDialog}
                legalAccountName={legalAccountName}
                terms={alphaTheoryTerms}
                termsVersion={alphaTheoryTermsVersion}
                isSuperuser={isSuperuser}
              />
            )
          } else if (integration.type === INTEGRATIONS.EZE) {
            return (
              <EzeIntegration
                key={i}
                status={ezeStatus}
                acceptPermission={ezeIntegrationPermission}
                account={account}
                user={user}
                openDialog={openDialog}
                closeDialog={closeDialog}
                legalAccountName={legalAccountName}
                terms={ezeTerms}
                termsVersion={ezeTermsVersion}
                isSuperuser={isSuperuser}
              />
            )
          } else if (integration.type === INTEGRATIONS.OMPNT_AI) {
            return (
              <AIIntegration
                key={i}
                isAiPermissionGranted={isAiPermissionGranted}
                aiIntegrationPermission={aiIntegrationPermission}
                account={account}
                user={user}
                openDialog={openDialog}
                closeDialog={closeDialog}
                legalAccountName={legalAccountName}
                terms={aiTerms}
                termsVersion={aiTermsVersion}
                isSuperuser={isSuperuser}
              />
            )
          }
        })}
      </div>
    </div>
  </div>
}

Platform.propTypes = {
  apps: PropTypes.arrayOf(APPLICATION).isRequired,
  deleteToken: PropTypes.func.isRequired,
  createToken: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  renameToken: PropTypes.func.isRequired,
  account: PropTypes.string.isRequired,
  user: USER.isRequired,
  openDialog: PropTypes.func.isRequired,
  closeDialog: PropTypes.func.isRequired,
  atIntegrationPermission: PropTypes.func.isRequired,
  alphaTheoryStatus: PropTypes.oneOf([NONE, REQUESTED, ACTIVE]),
  alphaTheoryTerms: PropTypes.string,
  alphaTheoryTermsVersion: PropTypes.number,
  ezeIntegrationPermission: PropTypes.func.isRequired,
  ezeStatus: PropTypes.oneOf([NONE, REQUESTED, ACTIVE]),
  ezeTerms: PropTypes.string,
  ezeTermsVersion: PropTypes.number,
  legalAccountName: PropTypes.string,
  hasApiAccess: PropTypes.bool.isRequired,
  isSuperuser: PropTypes.bool.isRequired,
  sortedIntegrations: PropTypes.array.isRequired,
  requestIntegration: PropTypes.func.isRequired,
  partnerTerms: TERMS_AND_CONDITIONS,
  activateApplication: PropTypes.func.isRequired,
  aiTerms: PropTypes.string,
  aiTermsVersion: PropTypes.number,
  aiIntegrationPermission: PropTypes.func.isRequired,
  isAiPermissionGranted: PropTypes.bool.isRequired,
}
